import { CERTIFICATE_TYPES } from '@ambernord/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { ShieldCheck } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

interface Coverage {
  rollingTurnover: number;
  requiredCoverageEUR: number;
  raiseWithinDays: number;
}

interface Certificate {
  id: string;
  type: string;
  identifier: string | null;
  validUntil: string | null;
}

interface SampleCheck {
  withinLimit: boolean;
  discountApplies: boolean;
  discount: number;
}

interface PostError {
  error?: string;
}

function eur(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function CoverageCalculator() {
  const { t, i18n } = useTranslation('insurance');
  const [turnover, setTurnover] = useState('');
  const [result, setResult] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    const n = Number(turnover);
    if (!Number.isFinite(n) || n < 0) {
      setError(t('coverage.invalidTurnover'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.get<Coverage>(`/api/insurance/coverage?rollingTurnover=${n}`);
      if (!res.ok) {
        setError(t('coverage.loadError', { status: res.status }));
        return;
      }
      setResult(res.data);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('coverage.heading')} subtitle={t('coverage.description')} />
      <CardBody>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('coverage.fields.turnover')} htmlFor="cov-turnover">
            <Input
              id="cov-turnover"
              type="number"
              min="0"
              step="any"
              required
              value={turnover}
              onChange={(e) => setTurnover(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={loading}>
            {loading ? t('coverage.submitting') : t('coverage.submit')}
          </Button>
        </form>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
        {result && (
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 dark:text-slate-400">
                {t('coverage.result.rollingTurnover')}
              </dt>
              <dd className="font-medium">{eur(result.rollingTurnover, i18n.language)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 dark:text-slate-400">
                {t('coverage.result.requiredCoverage')}
              </dt>
              <dd className="font-medium">{eur(result.requiredCoverageEUR, i18n.language)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 dark:text-slate-400">
                {t('coverage.result.raiseWithin')}
              </dt>
              <dd className="font-medium">
                {t('coverage.days', { count: result.raiseWithinDays })}
              </dd>
            </div>
          </dl>
        )}
      </CardBody>
    </Card>
  );
}

function Certificates() {
  const { t, i18n } = useTranslation('insurance');
  const { toast } = useToast();
  const [items, setItems] = useState<Certificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<string>(CERTIFICATE_TYPES[0]);
  const [identifier, setIdentifier] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await api.get<{ items: Certificate[] }>('/api/insurance/certificates');
    if (!res.ok) {
      setError(t('certificates.loadError', { status: res.status }));
      setItems([]);
      return;
    }
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: Certificate[] }>('/api/insurance/certificates');
      if (!active) return;
      if (!res.ok) {
        setError(t('certificates.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<PostError>('/api/insurance/certificates', {
        type,
        ...(identifier ? { identifier } : {}),
        ...(validUntil ? { validUntil: new Date(validUntil).toISOString() } : {}),
      });
      if (!res.ok) {
        const msg = res.data.error ?? t('errors.requestFailed', { status: res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setIdentifier('');
      setValidUntil('');
      toast(t('toast.certificateAdded'));
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('certificates.heading')} />
      <CardBody>
        <form
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('certificates.fields.type')} htmlFor="cert-type">
            <Select id="cert-type" value={type} onChange={(e) => setType(e.target.value)}>
              {CERTIFICATE_TYPES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('certificates.fields.identifier')} htmlFor="cert-identifier">
            <Input
              id="cert-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </Field>
          <Field label={t('certificates.fields.validUntil')} htmlFor="cert-valid">
            <Input
              id="cert-valid"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('saving') : t('certificates.submit')}
          </Button>
        </form>
        {formError && (
          <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}

        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title={t('certificates.empty.title')}
            hint={t('certificates.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('certificates.columns.type')}</Th>
                <Th>{t('certificates.columns.identifier')}</Th>
                <Th>{t('certificates.columns.validUntil')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((c) => (
                <Tr key={c.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">{c.type}</Td>
                  <Td>{c.identifier ?? '—'}</Td>
                  <Td>{formatDate(c.validUntil, i18n.language)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

function SampleLimitChecker() {
  const { t } = useTranslation('insurance');
  const [kg, setKg] = useState('');
  const [valueEUR, setValueEUR] = useState('');
  const [yearKg, setYearKg] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [result, setResult] = useState<SampleCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    const kgN = Number(kg);
    const vN = Number(valueEUR);
    const ykN = Number(yearKg);
    const yvN = Number(yearValue);
    if (![kgN, vN, ykN, yvN].every((n) => Number.isFinite(n) && n >= 0)) {
      setError(t('samples.invalidNumbers'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.get<SampleCheck>(
        `/api/insurance/samples/check?kg=${kgN}&valueEUR=${vN}&yearKg=${ykN}&yearValue=${yvN}`,
      );
      if (!res.ok) {
        setError(t('samples.loadError', { status: res.status }));
        return;
      }
      setResult(res.data);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('samples.heading')} subtitle={t('samples.description')} />
      <CardBody>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('samples.fields.kg')} htmlFor="smp-kg">
            <Input
              id="smp-kg"
              type="number"
              min="0"
              step="any"
              required
              value={kg}
              onChange={(e) => setKg(e.target.value)}
            />
          </Field>
          <Field label={t('samples.fields.value')} htmlFor="smp-value">
            <Input
              id="smp-value"
              type="number"
              min="0"
              step="any"
              required
              value={valueEUR}
              onChange={(e) => setValueEUR(e.target.value)}
            />
          </Field>
          <Field label={t('samples.fields.yearKg')} htmlFor="smp-yearkg">
            <Input
              id="smp-yearkg"
              type="number"
              min="0"
              step="any"
              required
              value={yearKg}
              onChange={(e) => setYearKg(e.target.value)}
            />
          </Field>
          <Field label={t('samples.fields.yearValue')} htmlFor="smp-yearvalue">
            <Input
              id="smp-yearvalue"
              type="number"
              min="0"
              step="any"
              required
              value={yearValue}
              onChange={(e) => setYearValue(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={loading}>
            {loading ? t('samples.submitting') : t('samples.submit')}
          </Button>
        </form>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
        {result && (
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 dark:text-slate-400">
                {t('samples.result.withinLimit')}
              </dt>
              <dd>
                <Badge tone={result.withinLimit ? 'green' : 'red'}>
                  {result.withinLimit ? t('yes') : t('no')}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 dark:text-slate-400">
                {t('samples.result.discountApplies')}
              </dt>
              <dd>
                <Badge tone={result.discountApplies ? 'green' : 'slate'}>
                  {result.discountApplies
                    ? t('samples.discountYes', { percent: Math.round(result.discount * 100) })
                    : t('no')}
                </Badge>
              </dd>
            </div>
          </dl>
        )}
      </CardBody>
    </Card>
  );
}

export default function Insurance() {
  const { t } = useTranslation('insurance');
  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-6">
        <CoverageCalculator />
        <Certificates />
        <SampleLimitChecker />
      </div>
    </AppLayout>
  );
}
